'use client';

import { useState, useEffect, useCallback } from 'react';
import { LawFirmUser } from '@/lib/api/account-api';
import { getLawFirmUsersAction } from '@/lib/services/account';

interface UseLawFirmUsersProps {
  autoRefresh?: boolean;
}

interface UseLawFirmUsersReturn {
  users: LawFirmUser[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  total: number;
}

export function useLawFirmUsers({
  autoRefresh = false
}: UseLawFirmUsersProps = {}): UseLawFirmUsersReturn {
  const [users, setUsers] = useState<LawFirmUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await getLawFirmUsersAction({ page: 1, limit: 100 });

      if (result.success && result.data) {
        // Show all users for assignment regardless of status
        setUsers(result.data.users);
        setTotal(result.data.users.length);
      } else {
        setError(
          (result as { error?: string }).error || 'Failed to fetch team members'
        );
        setUsers([]);
        setTotal(0);
      }
    } catch (_err) {
      setError('An unexpected error occurred while fetching team members');
      setUsers([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- the effect calls fetchUsers, whose setStates all sit behind an await; nothing runs synchronously with the effect body.
    fetchUsers();
  }, [fetchUsers]);

  // Auto-refresh functionality
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchUsers();
    }, 60000); // Refresh every minute

    return () => clearInterval(interval);
  }, [autoRefresh, fetchUsers]);

  return {
    users,
    loading,
    error,
    refetch: fetchUsers,
    total
  };
}
